import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  bulkCardArchiveBodySchema,
  bulkCardCopyBodySchema,
  bulkCardCopyResultSchema,
  bulkCardMoveBodySchema,
  bulkCardsChangedSchema,
  type BoardContents,
  type BulkCardArchiveBody,
  type BulkCardCopyBody,
  type BulkCardCopyResult,
  type BulkCardMoveBody,
  type BulkCardsChanged,
} from '@molt/shared';
import { announce } from './announce';
import { post } from './api';
import { setBoardContentsQueries } from './cross-board-move';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys } from './query-keys';

export interface BulkArchiveCardsVariables {
  readonly boardId: string;
  readonly cardIds: readonly string[];
}

export interface BulkMoveCardsVariables {
  readonly boardId: string;
  readonly destinationBoardId: string;
  readonly cardIds: readonly string[];
  readonly listId: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
  readonly destinationListTitle?: string | undefined;
}

export interface BulkCopyCardsVariables extends BulkMoveCardsVariables {
  readonly keepChecklists: boolean;
  readonly keepLabels: boolean;
  readonly keepMembers: boolean;
  readonly keepAttachments: boolean;
  readonly keepDates: boolean;
}

export function bulkArchiveCards(
  boardId: string,
  body: BulkCardArchiveBody,
): Promise<BulkCardsChanged> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/cards/bulk-archive`,
    bulkCardsChangedSchema,
    bulkCardArchiveBodySchema.parse(body),
  );
}

export function bulkMoveCards(
  boardId: string,
  body: BulkCardMoveBody,
): Promise<BulkCardsChanged> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/cards/bulk-move`,
    bulkCardsChangedSchema,
    bulkCardMoveBodySchema.parse(body),
  );
}

export function bulkCopyCards(
  boardId: string,
  body: BulkCardCopyBody,
): Promise<BulkCardCopyResult> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/cards/bulk-copy`,
    bulkCardCopyResultSchema,
    bulkCardCopyBodySchema.parse(body),
  );
}

export function applyBulkArchiveResult(
  previous: BoardContents | undefined,
  variables: BulkArchiveCardsVariables,
  result: BulkCardsChanged,
): BoardContents | undefined {
  if (previous === undefined) return undefined;
  const archived = new Set(result.cards.map((card) => card.id));
  return {
    ...previous,
    lists: previous.lists.map((list) => {
      const cards = list.cards.filter((card) => !archived.has(card.id));
      if (cards.length === list.cards.length) return list;
      const removed = list.cards.length - cards.length;
      return {
        ...list,
        cards,
        cardCount: Math.max(0, list.cardCount - removed),
        openCardCount: Math.max(0, list.openCardCount - removed),
      };
    }),
  };
}

export function applyBulkMoveResult(
  previous: BoardContents | undefined,
  variables: BulkMoveCardsVariables,
  result: BulkCardsChanged,
): BoardContents | undefined {
  if (previous === undefined) return undefined;
  const movedIds = result.cards.map((card) => card.id);
  const moved = new Set(movedIds);
  const cardsById = new Map<string, BoardContents['lists'][number]['cards'][number]>();

  for (const list of previous.lists) {
    for (const card of list.cards) {
      if (moved.has(card.id)) cardsById.set(card.id, card);
    }
  }

  return {
    ...previous,
    lists: previous.lists.map((list) => {
      const remainingCards = list.cards.filter((card) => !moved.has(card.id));
      const removed = list.cards.length - remainingCards.length;
      const base =
        removed === 0
          ? list
          : {
              ...list,
              cards: remainingCards,
              cardCount: Math.max(0, list.cardCount - removed),
              openCardCount: Math.max(0, list.openCardCount - removed),
            };

      if (list.id !== variables.listId) return base;

      const movedCards = movedIds
        .map((id) => cardsById.get(id))
        .filter(
          (card): card is BoardContents['lists'][number]['cards'][number] =>
            card !== undefined,
        )
        .map((card) => ({ ...card, listId: variables.listId }));

      return {
        ...base,
        cards: insertCardsAtNeighbours(
          base.cards,
          movedCards,
          variables.previousCardId,
          variables.nextCardId,
        ),
        cardCount: base.cardCount + movedCards.length,
        openCardCount: base.openCardCount + movedCards.length,
      };
    }),
  };
}

export function applyBulkCopyResult(
  previous: BoardContents | undefined,
  variables: BulkCopyCardsVariables,
  result: BulkCardCopyResult,
): BoardContents | undefined {
  void variables;
  void result;
  return previous;
}

function insertCardsAtNeighbours<T extends { id: string }>(
  cards: readonly T[],
  inserting: readonly T[],
  previousCardId: string | null,
  nextCardId: string | null,
): T[] {
  if (previousCardId === null && nextCardId === null) {
    return [...cards, ...inserting];
  }

  if (previousCardId === null) {
    const nextIndex = cards.findIndex((card) => card.id === nextCardId);
    return nextIndex === -1
      ? [...inserting, ...cards]
      : [...cards.slice(0, nextIndex), ...inserting, ...cards.slice(nextIndex)];
  }

  const previousIndex = cards.findIndex((card) => card.id === previousCardId);
  if (previousIndex === -1) return [...cards, ...inserting];
  const insertIndex = previousIndex + 1;
  return [...cards.slice(0, insertIndex), ...inserting, ...cards.slice(insertIndex)];
}

function invalidateBulkCardSurfaces(
  queryClient: QueryClient,
  boardId: string,
  cardIds: readonly string[],
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.contents(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.table(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.calendar(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.timeline(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.dashboard(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.map(boardId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.boards.capacity(boardId) });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.activity(boardId, 'all'),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.boards.activity(boardId, 'comments'),
  });
  for (const cardId of cardIds) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
  }
}

function invalidateBulkDestinationSurfaces(
  queryClient: QueryClient,
  variables: Pick<BulkMoveCardsVariables, 'boardId' | 'destinationBoardId'>,
  cardIds: readonly string[],
): void {
  if (variables.destinationBoardId === variables.boardId) return;
  invalidateBulkCardSurfaces(queryClient, variables.destinationBoardId, cardIds);
}

export function useBulkArchiveCards(): UseMutationResult<
  BulkCardsChanged,
  unknown,
  BulkArchiveCardsVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      bulkArchiveCards(variables.boardId, { cardIds: [...variables.cardIds] }),
    onSuccess: (result, variables) => {
      setBoardContentsQueries(queryClient, variables.boardId, (previous) =>
        applyBulkArchiveResult(previous, variables, result),
      );
      invalidateBulkCardSurfaces(queryClient, variables.boardId, variables.cardIds);
      announce(
        t(
          'primitives.cardBulkActions.archiveSuccess',
          { count: result.count },
          result.count,
        ),
      );
    },
    onError: (error) => {
      announce(
        error instanceof Error
          ? error.message
          : t('primitives.cardBulkActions.archiveFailed'),
      );
    },
  });
}

export function useBulkMoveCards(): UseMutationResult<
  BulkCardsChanged,
  unknown,
  BulkMoveCardsVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      bulkMoveCards(variables.boardId, {
        cardIds: [...variables.cardIds],
        listId: variables.listId,
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
      }),
    onSuccess: (result, variables) => {
      setBoardContentsQueries(queryClient, variables.boardId, (previous) =>
        applyBulkMoveResult(previous, variables, result),
      );
      invalidateBulkCardSurfaces(queryClient, variables.boardId, variables.cardIds);
      invalidateBulkDestinationSurfaces(queryClient, variables, variables.cardIds);
      const key =
        variables.destinationListTitle === undefined
          ? 'primitives.cardBulkActions.moveSuccess'
          : 'primitives.cardBulkActions.moveSuccessTo';
      announce(
        t(
          key,
          { count: result.count, list: variables.destinationListTitle ?? '' },
          result.count,
        ),
      );
    },
    onError: (error) => {
      announce(
        error instanceof Error
          ? error.message
          : t('primitives.cardBulkActions.moveFailed'),
      );
    },
  });
}

export function useBulkCopyCards(): UseMutationResult<
  BulkCardCopyResult,
  unknown,
  BulkCopyCardsVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      bulkCopyCards(variables.boardId, {
        cardIds: [...variables.cardIds],
        listId: variables.listId,
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
        keepChecklists: variables.keepChecklists,
        keepLabels: variables.keepLabels,
        keepMembers: variables.keepMembers,
        keepAttachments: variables.keepAttachments,
        keepDates: variables.keepDates,
      }),
    onSuccess: (result, variables) => {
      setBoardContentsQueries(queryClient, variables.boardId, (previous) =>
        applyBulkCopyResult(previous, variables, result),
      );
      invalidateBulkCardSurfaces(queryClient, variables.boardId, variables.cardIds);
      invalidateBulkDestinationSurfaces(
        queryClient,
        variables,
        result.cards.map((card) => card.id),
      );
      const key =
        variables.destinationListTitle === undefined
          ? 'primitives.cardBulkActions.copySuccess'
          : 'primitives.cardBulkActions.copySuccessTo';
      announce(
        t(
          key,
          { count: result.count, list: variables.destinationListTitle ?? '' },
          result.count,
        ),
      );
    },
    onError: (error) => {
      announce(
        error instanceof Error
          ? error.message
          : t('primitives.cardBulkActions.copyFailed'),
      );
    },
  });
}
